import { SpaceBetween, Icon, Button, Header, Alert, Link } from '@amzn/awsui-components-react';
import { IneligibilityReasons } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useGetAmbassadors } from '../../hooks/ambassadors/getAmbassadors';
import { useUser } from '../../hooks/user';
import { AMBASSADOR_EXTERNAL_PLAN_EVENT_LINK } from '../../utils/constants';

interface AmbassadorRegistrationFailureProps {
  ineligibilityReasons: IneligibilityReasons | undefined;
}

export const AmbassadorRegistrationSuccess = () => {
  const { t } = useTranslation(['ambassadors', 'translation']);
  const ambassadors = useGetAmbassadors();
  const loggedInUser = useUser();

  const ambassadorsInArea = ambassadors?.data?.filter((i) => {
    const ambassadorIsInSameCityAsLoggedInUser = i.city === loggedInUser.employeeCity;

    return ambassadorIsInSameCityAsLoggedInUser;
  });

  return (
    <SpaceBetween direction="vertical" size="s" alignItems="center">
      <Icon name="status-positive" size="large" variant="success" />
      <Header data-testid="ambassador-registration-success-header" variant="h1">
        {t('ambassadorRegistration.result.success.successTitle')}
      </Header>
      <Alert
        statusIconAriaLabel="Info"
        header={
          ambassadorsInArea?.length
            ? t('ambassadorRegistration.result.success.ambassadorsInYourAreaWithCount', {
                count: ambassadorsInArea?.length,
              })
            : t('ambassadorRegistration.result.success.ambassadorsInYourArea')
        }
      >
        <SpaceBetween direction="vertical" size="xxxs">
          {t('ambassadorRegistration.result.success.teamUp')}
          <Link href="/ambassadors?in-your-area=true" target="_blank">
            {t('ambassadorRegistration.result.success.viewAmbassadors')}
          </Link>
        </SpaceBetween>
      </Alert>
      <div style={{ justifyItems: 'center' }}>
        <SpaceBetween direction="horizontal" size="s">
          <Button href={AMBASSADOR_EXTERNAL_PLAN_EVENT_LINK} variant="normal" external>
            {t('ambassadorRegistration.result.success.planEvent')}
          </Button>
        </SpaceBetween>
      </div>
    </SpaceBetween>
  );
};

export const AmbassadorRegistrationFailure = ({
  ineligibilityReasons,
}: AmbassadorRegistrationFailureProps) => {
  const { t } = useTranslation(['ambassadors', 'translation']);
  const [sixMonthsDate, setSixMonthsDate] = useState<string>(
    t('ambassadorRegistration.result.error.sixMonthsFromTheDate')
  );

  useEffect(() => {
    const fetchDate = async () => {
      try {
        if (ineligibilityReasons?.tenure?.eligibleValue) {
          const updatedDate = new Date(ineligibilityReasons?.tenure?.eligibleValue);
          const formattedDate = updatedDate.toLocaleString('en-US', {
            year: 'numeric',
            day: 'numeric',
            month: 'long',
          });
          setSixMonthsDate(formattedDate);
        }
      } catch (error) {
        console.warn(error);
      }
    };
    fetchDate();
  }, [t, ineligibilityReasons?.tenure?.eligibleValue]);

  const alertIneligibilityReasons = () => {
    const alertValues = {
      employmentStatus: {
        title: t('ambassadorRegistration.result.error.employmentStatus'),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetEmploymentStatus'),
      },
      badgeColor: {
        title: t('ambassadorRegistration.result.error.badgeColor'),
        action: t('ambassadorRegistration.result.error.youNeedBlueBadge'),
      },
      jobLevel: {
        title: t('ambassadorRegistration.result.error.jobLevel'),
        action: t('ambassadorRegistration.result.error.jobLevelNotHighEnough'),
      },
      tenure: {
        title: t('ambassadorRegistration.result.error.reapplyIn', {
          reapplyDate: sixMonthsDate,
        }),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetTenure'),
      },
    };

    if (!ineligibilityReasons) return null;

    return Object.keys(ineligibilityReasons as object).map((key, index) => {
      return (
        <li key={index}>
          <b>{alertValues[key as keyof IneligibilityReasons]?.title}</b> -{' '}
          {alertValues[key as keyof IneligibilityReasons]?.action}
        </li>
      );
    });
  };

  const whatYouCanDoListItems = () => {
    const listItemValues = {
      employmentStatus: {
        title: t('ambassadorRegistration.result.error.employmentStatus'),
        action: t('ambassadorRegistration.result.error.submitOnceYouveMetEmploymentStatus'),
      },
      badgeColor: {
        title: t('ambassadorRegistration.result.error.badgeColor'),
        action: t('ambassadorRegistration.result.error.submitOnceBlueBadgeAndManagerApproves'),
      },
      jobLevel: {
        title: t('ambassadorRegistration.result.error.jobLevel'),
        action: t('ambassadorRegistration.result.error.submitOnceL3AndManagerApproves'),
      },
      tenure: {
        title: t('ambassadorRegistration.result.error.reapplyIn', {
          reapplyDate: sixMonthsDate,
        }),
        action: t('ambassadorRegistration.result.error.submitOnceTenureAndManagerApproves'),
      },
    };

    if (!ineligibilityReasons) return null;

    return Object.keys(ineligibilityReasons as object).map((key, index) => {
      return (
        <li key={index}>
          <b>{listItemValues[key as keyof IneligibilityReasons]?.title}</b>
          <br />
          {listItemValues[key as keyof IneligibilityReasons]?.action}
        </li>
      );
    });
  };

  return (
    <SpaceBetween direction="vertical" size="s" data-ambassador-registration-failure-container>
      <SpaceBetween direction="vertical" size="s" alignItems="center">
        <Icon name="status-warning" size="large" variant="error" />
        <Header variant="h1">{t('ambassadorRegistration.result.error.errorTitle')}</Header>
        <p>{t('ambassadorRegistration.result.error.errorDetail')}</p>
        <Alert
          data-testId="ambassador-registration-failure-alert"
          statusIconAriaLabel="Warning"
          type="warning"
          header={t('ambassadorRegistration.result.error.followingReasons')}
        >
          <SpaceBetween direction="vertical" size="xxxs">
            <ul style={{ paddingLeft: 0 }}>{alertIneligibilityReasons()}</ul>
          </SpaceBetween>
        </Alert>
        <p>
          {t('ambassadorRegistration.result.error.ifYouBelieve')}
          <Link
            href="https://t.corp.amazon.com/create/templates/9cedc919-f013-43b0-9e96-e096bd8bbb5a"
            target="_blank"
          >
            {t('ambassadorRegistration.result.error.supportTool')}
          </Link>
        </p>
      </SpaceBetween>
      <div style={{ width: '100%' }}>
        <Header variant="h3">{t('ambassadorRegistration.result.error.whatYouCanDo')}</Header>
        <ol style={{ paddingLeft: 0, listStyle: 'outside inside', padding: '0 16px' }}>
          {whatYouCanDoListItems()}
        </ol>
      </div>
    </SpaceBetween>
  );
};
